import React, { useEffect, useState } from 'react';
import RecipeCard from './RecipeCard';
import { Spinner } from 'react-bootstrap';
import { useGetAllFavoriteRecipes } from '../hooks/favoriteRecipe';
import { useGetAllDislikedRecipes } from '../hooks/dislikeRecipe';
import { useRecipeCombinations } from '../hooks/useRecipeCombinations';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { useAuth } from '../context/AuthContext';
import ErrorBoundary from './ErrorBoundary';
import { getIngredientImageUrl } from '../utils/imageUtils';
import AddPantryIngredientButton from './AddPantryIngredientButton';

const RecipesForPantry = ({ searchTerm = '', onMealAdded }) => {
    const { getAllFavoriteRecipes } = useGetAllFavoriteRecipes();
    const { getAllDislikedRecipes } = useGetAllDislikedRecipes();
    const { fetchRecipeCombinations, combinationResults, isLoading: isLoadingCombinations } = useRecipeCombinations();
    const [favoriteRecipes, setFavoriteRecipes] = useState([]);
    const [dislikedRecipes, setDislikedRecipes] = useState([]);
    const [pantryIngredients, setPantryIngredients] = useState([]);
    const [selectedIngredientIds, setSelectedIngredientIds] = useState([]);
    const [isLoadingPantry, setIsLoadingPantry] = useState(true);
    const { sessionToken, pgUser } = useAuth();

    // Deduplicate ingredients by ingredient_id
    const deduplicateIngredients = (ingredients) => {
        const seen = new Set();
        return ingredients.filter(ingredient => {
            if (seen.has(ingredient.ingredient_id)) {
                return false;
            }
            seen.add(ingredient.ingredient_id);
            return true;
        });
    };

    // Fetch pantry ingredients
    useEffect(() => {
        const fetchPantryIngredients = async () => {
            if (!sessionToken || !pgUser?.pantry_id || !pgUser?.id) return;
            
            setIsLoadingPantry(true);
            try {
                const response = await fetchWithCSRF(`/api/v1/pantry-ingredient/pantry/${pgUser.pantry_id}/app_user/${pgUser.id}`, {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) {
                        setPantryIngredients(deduplicateIngredients(data));
                    }
                }
            } catch (error) {
                console.error('Error fetching pantry ingredients:', error);
            } finally {
                setIsLoadingPantry(false);
            }
        };

        fetchPantryIngredients();
    }, [sessionToken, pgUser?.pantry_id, pgUser?.id]);

    // Fetch recipes based on selected ingredients
    useEffect(() => {
        if (selectedIngredientIds.length >= 1) {
            fetchRecipeCombinations(selectedIngredientIds);
        }
    }, [selectedIngredientIds, fetchRecipeCombinations]);

    // Toggle ingredient selection
    const toggleIngredient = (ingredientId) => {
        setSelectedIngredientIds(prev => {
            if (prev.includes(ingredientId)) {
                return prev.filter(id => id !== ingredientId);
            } else {
                return [...prev, ingredientId];
            }
        });
    };

    // Get ingredient names from IDs
    const getIngredientNames = (ingredientIds) => {
        return ingredientIds
            .map(id => {
                // Compare as strings (IDs are UUIDs)
                const ingredient = pantryIngredients.find(i => String(i.ingredient_id) === String(id));
                return ingredient?.ingredient_name;
            })
            .filter(Boolean);
    };

    // Get ingredient data (with IDs for images) from IDs
    const getIngredientData = (ingredientIds) => {
        return ingredientIds
            .map(id => {
                const ingredient = pantryIngredients.find(i => String(i.ingredient_id) === String(id));
                return ingredient ? {
                    id: ingredient.ingredient_id,
                    name: ingredient.ingredient_name
                } : null;
            })
            .filter(Boolean);
    };

    const formatIngredientList = (names) => {
        if (names.length === 0) return "";
        if (names.length === 1) return names[0];
        if (names.length === 2) return `${names[0]} and ${names[1]}`;
        return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
    };

    // Fetch favorite and disliked recipes
    useEffect(() => {
        if (!sessionToken) return;
        
        const fetchFavoritesAndDislikes = async () => {
            try {
                // Fetch favorites
                const favoritesData = await getAllFavoriteRecipes(sessionToken);
                if (Array.isArray(favoritesData)) {
                    setFavoriteRecipes(favoritesData);
                }

                // Fetch dislikes
                const dislikesData = await getAllDislikedRecipes(sessionToken);
                if (Array.isArray(dislikesData)) {
                    setDislikedRecipes(dislikesData);
                }
            } catch (error) {
                console.error('Error fetching favorites/dislikes:', error);
            }
        };
        fetchFavoritesAndDislikes();
    }, [sessionToken, getAllFavoriteRecipes, getAllDislikedRecipes]);

    // Filter combination results based on search term
    const getFilteredCombinations = () => {
        if (!searchTerm) return combinationResults;
        
        const searchLower = searchTerm.toLowerCase();
        return combinationResults.map(combo => ({
            ...combo,
            recipes: combo.recipes.filter(recipe => {
                // Search by recipe name
                if (recipe.recipe_name?.toLowerCase().includes(searchLower)) {
                    return true;
                }
                
                // Search by description
                if (recipe.description?.toLowerCase().includes(searchLower)) {
                    return true;
                }
                
                return false;
            })
        })).filter(combo => combo.recipes.length > 0);
    };

    const filteredCombinations = getFilteredCombinations();

    const handlePantryIngredientAdded = async () => {
        // Refetch pantry ingredients when a new ingredient is added
        if (sessionToken && pgUser?.pantry_id && pgUser?.id) {
            try {
                const response = await fetchWithCSRF(`/api/v1/pantry-ingredient/pantry/${pgUser.pantry_id}/app_user/${pgUser.id}`, {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) {
                        setPantryIngredients(deduplicateIngredients(data));
                    }
                }
            } catch (error) {
                console.error('Error fetching pantry ingredients:', error);
            }
        }
    };

    return (
        <ErrorBoundary fallbackMessage="Unable to load pantry recipes. Please try again later.">
            {/* <div className="d-flex align-items-center" style={{ paddingLeft: '15px', paddingRight: '15px', marginBottom: '15px', marginTop: '25px', flexWrap: 'wrap', gap: '10px' }}>
                <h2 style={{ marginRight: '0px', marginBottom: '0px' }}>Recipes Based on Your Pantry</h2>
            </div> */}

            {isLoadingPantry ? (
                <div className="container">
                    <div className="text-center my-4">
                        <Spinner animation="border" role="status" style={{ color: '#52cd92' }}>
                            <span className="visually-hidden">Loading pantry ingredients...</span>
                        </Spinner>
                    </div>
                </div>
            ) : (
                <>
                    {/* Ingredient Pills - Keep in container */}
                    <div className="container">
                        <div style={{ paddingLeft: '15px', paddingRight: '15px', marginBottom: '20px' }}>
                            {/* <p style={{ marginBottom: '10px', fontWeight: '500', color: '#666' }}>
                                Select ingredients to find recipes:
                            </p> */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                {[...pantryIngredients]
                                    .sort((a, b) => a.ingredient_name.length - b.ingredient_name.length)
                                    .map(ingredient => {
                                    const isSelected = selectedIngredientIds.includes(ingredient.ingredient_id);
                                    return (
                                        <button
                                            key={ingredient.ingredient_id}
                                            onClick={() => toggleIngredient(ingredient.ingredient_id)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '8px 8px 8px 8px',
                                                borderRadius: '25px',
                                                border: isSelected ? '2px solid #52cd92' : '2px solid #e0e0e0',
                                                backgroundColor: isSelected ? '#52cd92' : '#ffffff',
                                                color: isSelected ? '#ffffff' : '#333333',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                outline: 'none'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isSelected) {
                                                    e.currentTarget.style.borderColor = '#52cd92';
                                                    e.currentTarget.style.backgroundColor = '#f0fdf7';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isSelected) {
                                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                                    e.currentTarget.style.backgroundColor = '#ffffff';
                                                }
                                            }}
                                        >
                                            <img 
                                                src={getIngredientImageUrl(ingredient.ingredient_id)}
                                                alt={ingredient.ingredient_name}
                                                style={{
                                                    width: '24px',
                                                    height: '24px',
                                                    // borderRadius: '50%',
                                                    objectFit: 'cover',
                                                }}
                                                onError={(e) => { 
                                                    e.target.onerror = null; 
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                            {ingredient.ingredient_name}
                                        </button>
                                    );
                                })}
                                
                                {/* Add Ingredient Button Pill */}
                                {pgUser?.pantry_id && pgUser?.id && (
                                    <AddPantryIngredientButton
                                        pantryId={pgUser.pantry_id}
                                        appUserId={pgUser.id}
                                        onSubmit={handlePantryIngredientAdded}
                                        buttonText="+ Add Ingredient"
                                        buttonClassName=""
                                        buttonStyle={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '8px 16px',
                                            borderRadius: '25px',
                                            border: '2px solid #e0e0e0',
                                            backgroundColor: '#ddd',
                                            color: '#333333',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            outline: 'none',
                                            transition: 'all 0.2s ease'
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Recipes Display by Combination - Break out of container */}
                    {selectedIngredientIds.length === 0 ? (
                        <div className="container">
                            <div className="text-center my-5" style={{ color: '#666' }}>
                                {pantryIngredients.length === 0 ? (
                                    <p>Add ingredients to your pantry to see recipe suggestions!</p>
                                ) : (
                                    <p>Select one or more ingredients above to see recipes</p>
                                )}
                            </div>
                        </div>
                    ) : isLoadingCombinations ? (
                        <div className="container">
                            <div className="text-center my-4">
                                <Spinner animation="border" role="status" style={{ color: '#52cd92' }}>
                                    <span className="visually-hidden">Loading recipes...</span>
                                </Spinner>
                            </div>
                        </div>
                    ) : filteredCombinations.length === 0 ? (
                        <div className="container">
                            <div className="text-center my-5" style={{ color: '#666' }}>
                                <p>No recipes found with the selected ingredients{searchTerm ? ` matching "${searchTerm}"` : ''}</p>
                            </div>
                        </div>
                    ) : (
                        <div>
                                {filteredCombinations.map((combo, index) => {
                                    const comboKey = combo.ingredientIds.join(',');
                                    const ingredientData = getIngredientData(combo.ingredientIds);
                                    
                                    return (
                                        <div key={comboKey} className="combination-section mb-5">
                                            {/* Combination Header */}
                                            <div 
                                                className="combination-header"
                                                style={{ 
                                                    paddingLeft: '30px',
                                                    paddingRight: '15px',
                                                    paddingTop: '12px',
                                                    paddingBottom: '12px',
                                                    // backgroundColor: '#f8f9fa',
                                                    borderRadius: '8px',
                                                    // border: '1px solid #e0e0e0',
                                                    marginBottom: '15px'
                                                }}
                                            >
                                                <div style={{ textAlign: 'left' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                        <h4 className="mb-0" style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                                                            Recipes with
                                                        </h4>
                                                        {ingredientData.map((ingredient, idx) => (
                                                            <React.Fragment key={ingredient.id}>
                                                                <div style={{ 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    gap: '8px',
                                                                    padding: '6px 14px',
                                                                    borderRadius: '20px',
                                                                    border: '2px solid #52cd92',
                                                                    backgroundColor: '#52cd92',
                                                                    color: '#ffffff',
                                                                    fontSize: '0.95rem',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    <img 
                                                                        src={getIngredientImageUrl(ingredient.id)}
                                                                        alt={ingredient.name}
                                                                        style={{
                                                                            width: '20px',
                                                                            height: '20px',
                                                                            // borderRadius: '50%',
                                                                            objectFit: 'cover',
                                                                        }}
                                                                        onError={(e) => { 
                                                                            e.target.onerror = null; 
                                                                            e.target.style.display = 'none';
                                                                        }}
                                                                    />
                                                                    {ingredient.name}
                                                                </div>
                                                                {idx < ingredientData.length - 1 && (
                                                                    <span style={{ fontSize: '1.1rem', fontWeight: '500', color: '#666' }}>
                                                                        {idx === ingredientData.length - 2 ? 'and' : '+'}
                                                                    </span>
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                    </div>
                                                    <small className="text-muted" style={{ display: 'block', marginTop: '8px' }}>
                                                        {combo.recipeCount} recipe{combo.recipeCount !== 1 ? 's' : ''}
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Horizontal Scrollable Recipes Row */}
                                            <div 
                                                style={{ 
                                                    display: 'flex',
                                                    overflowX: 'auto',
                                                    overflowY: 'hidden',
                                                    gap: '20px',
                                                    paddingLeft: '30px',
                                                    paddingBottom: '10px',
                                                    scrollbarWidth: 'thin',
                                                    scrollbarColor: '#313638 #e0e0e0',
                                                    WebkitOverflowScrolling: 'touch'
                                                }}
                                                className="horizontal-recipe-scroll"
                                            >
                                                {combo.recipes.map(recipe => (
                                                    <div 
                                                        key={recipe.recipe_id}
                                                        style={{
                                                            minWidth: '260px',
                                                            maxWidth: '260px',
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        <RecipeCard 
                                                            recipe={{
                                                                id: recipe.recipe_id,
                                                                name: recipe.recipe_name,
                                                                description: recipe.description,
                                                                prep_time: recipe.prep_time,
                                                                cook_time: recipe.cook_time,
                                                                total_time: recipe.total_time,
                                                                servings: recipe.servings,
                                                                difficulty: recipe.difficulty,
                                                                instructions: recipe.instructions,
                                                                image_url: recipe.image_url,
                                                                is_vegetarian: recipe.is_vegetarian,
                                                                is_vegan: recipe.is_vegan,
                                                                protein: recipe.protein,
                                                                carbohydrate: recipe.carbohydrate,
                                                                fat: recipe.fat,
                                                                calories: recipe.calories,
                                                                micronutrients: recipe.micronutrients,
                                                                cost_per_serving: recipe.cost_per_serving,
                                                                taste_profile: recipe.taste_profile,
                                                                tags: recipe.tags,
                                                                equipment: recipe.equipment,
                                                                created_at: recipe.created_at,
                                                                updated_at: recipe.updated_at,
                                                                ingredients: recipe.ingredients || []
                                                            }}
                                                            isFavorite={Array.isArray(favoriteRecipes) && favoriteRecipes.includes(Number(recipe.recipe_id))}
                                                            isDisliked={Array.isArray(dislikedRecipes) && dislikedRecipes.includes(Number(recipe.recipe_id))}
                                                            pantryIngredients={pantryIngredients}
                                                            onMealAdded={onMealAdded}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                </>
            )}
        </ErrorBoundary>
    );
};

export default RecipesForPantry;

